import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCZK, formatTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Users, CalendarDays, UserMinus, UsersRound } from "lucide-react";
import { Avatar } from "@/components/shared/Avatar";
import { PermissionsMatrix } from "@/components/shared/PermissionsMatrix";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

const roleLabel = (r) => r === "OWNER" ? "Majitel" : r === "MANAGER" ? "Stavbyvedoucí" : r === "ASSISTANT" ? "Asistent/ka" : r === "SUBCONTRACTOR" ? "Subdodavatel" : r === "DESIGNER" ? "Projektant" : "Dělník";
const avatarCls = (r) => r === "OWNER" || r === "MANAGER" ? "bg-brand/15 text-brand"
  : r === "SUBCONTRACTOR" ? "bg-secondary text-muted-foreground" : "bg-success/15 text-success";
const fmtH = (h) => `${(h || 0).toFixed(1).replace(".", ",")} h`;

export function ProjectTeamTab({ projectId, proj, eco, user, manager, teamAll, onManage, onRemove, onReload }) {
  const nav = useNavigate();
  const [workers, setWorkers] = useState([]);   // {user_id, name, hours, cost?}
  const [live, setLive] = useState(null);
  const [cov, setCov] = useState(null);         // řádek pokrytí této zakázky (169)
  const [addAllOpen, setAddAllOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const seeMoney = user.role === "OWNER" || user.role === "ASSISTANT";

  const loadCov = () => {
    if (!manager) return;
    // 219: jen řádek této zakázky (dřív pokrytí všech aktivních zakázek a jejich docházky)
    api.get("/projects/team-coverage", { params: { project_id: projectId } })
      .then((r) => setCov((r.data.projects || []).find((p) => p.id === projectId) || null))
      .catch(() => {});
  };

  useEffect(() => {
    if (!manager) return;
    api.get(`/attendance/overview?project_ids=${projectId}&group=worker&approved=true`).then((r) => setWorkers(r.data.rows || [])).catch(() => {});
    api.get(`/projects/${projectId}/live`).then((r) => setLive(r.data)).catch(() => {});
    loadCov();
  }, [projectId, manager]); // eslint-disable-line

  const missing = cov?.missing || [];
  const anm = cov?.attendance_not_member || [];
  const plur = (n) => (n === 1 ? "člověk má" : n < 5 ? "lidé mají" : "lidí má");

  const addAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/team/add-all`);
      toast.success(`Přidáno ${data.pairs} lidí.`);
      setAddAllOpen(false);
      onReload?.();
      loadCov();
    } catch { toast.error("Přidání se nepovedlo."); }
    finally { setBusy(false); }
  };

  const addAnm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects/team/bulk", {
        mode: "add", project_ids: [projectId], user_ids: anm.map((a) => a.id), dry_run: false });
      toast.success(`Přidáno ${data.pairs} lidí.`);
      onReload?.();
      loadCov();
    } catch { toast.error("Přidání se nepovedlo."); }
    finally { setBusy(false); }
  };

  const byId = Object.fromEntries(workers.map((w) => [w.user_id, w]));
  const clockedById = Object.fromEntries((live?.clocked_in || []).map((c) => [c.user_id, c]));
  const rateById = Object.fromEntries((teamAll || []).map((t) => [t.id, t.hourly_rate]));

  const members = [...(proj.team_users || [])].sort((a, b) => {
    const ca = clockedById[a.id] ? 0 : 1, cb = clockedById[b.id] ? 0 : 1;
    if (ca !== cb) return ca - cb;
    const sa = a.role === "SUBCONTRACTOR" ? 1 : 0, sb = b.role === "SUBCONTRACTOR" ? 1 : 0;
    if (sa !== sb) return sa - sb;
    return (byId[b.id]?.hours || 0) - (byId[a.id]?.hours || 0);
  });

  const cat = eco?.by_category || {};
  const own = cat["Práce"] || 0;
  const sub = cat["Subdodávka"] || 0;
  const splitTotal = own + sub;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4 items-start" data-testid="project-team-tab">
      {/* Lidé na zakázce */}
      <div className="bg-card border border-border rounded-[14px] p-4 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="text-[15px] font-bold">Lidé na zakázce</h3>
          {manager && (
            <div className="flex items-center gap-2">
              {missing.length > 0 && (
                <Button variant="outline" onClick={() => setAddAllOpen(true)} data-testid="add-all-company-btn"
                  className="h-[34px] rounded-[10px] font-bold text-[13px]">
                  <UsersRound size={14} /> Přidat všechny z firmy
                </Button>
              )}
              <Button onClick={onManage} data-testid="manage-team-btn" className="h-[34px] rounded-[10px] font-bold text-[13px]">
                <Users size={14} /> Přidat člověka
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {members.map((u) => {
            const c = clockedById[u.id];
            const w = byId[u.id];
            const rate = rateById[u.id];
            const isSub = u.role === "SUBCONTRACTOR";
            return (
              <div key={u.id} data-testid={`team-row-${u.id}`}
                className={`rounded-[12px] border px-3.5 py-3 flex items-center gap-3.5 ${c ? "border-success/40 bg-success/5" : "border-border/70"}`}>
                <Avatar name={u.name} photo={u.photo_url} size={36} className={avatarCls(u.role)} testid={`team-avatar-${u.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[15px] font-bold truncate">{u.name}</span>
                    {c && <span className="rounded-full bg-success text-white px-2 py-0.5 text-[11px] font-bold whitespace-nowrap">Na stavbě od {formatTime(c.check_in)}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {roleLabel(u.role)}{seeMoney && rate && !isSub ? ` · ${formatCZK(rate)}/h` : ""}
                  </div>
                </div>
                {manager && (
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold tabular-nums">{isSub ? "—" : fmtH(w?.hours || 0)}</div>
                    {seeMoney && !isSub && <div className="text-xs text-muted-foreground tabular-nums">{formatCZK(w?.cost || 0)}</div>}
                  </div>
                )}
                {manager && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => nav(`/dochazka/vykaz/${u.id}`)} title="Zobrazit docházku"
                      className="p-2 rounded-[8px] text-muted-foreground hover:bg-secondary" data-testid={`team-attendance-${u.id}`}>
                      <CalendarDays size={16} />
                    </button>
                    <button onClick={async () => { if (await ask(`Odebrat ${u.name} ze zakázky? Odpracované hodiny zůstávají.`)) onRemove(u.id); }}
                      title="Odebrat ze zakázky" className="p-2 rounded-[8px] text-muted-foreground hover:text-destructive hover:bg-secondary" data-testid={`team-remove-${u.id}`}>
                      <UserMinus size={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {members.length === 0 && <p className="text-sm text-muted-foreground">Žádní členové týmu.</p>}
        </div>
        {manager && anm.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border text-[13px] text-muted-foreground" data-testid="attendance-not-member-hint">
            {anm.length} {plur(anm.length)} na zakázce docházku, ale nejsou v týmu: {anm.map((a) => a.name).join(", ")}.{" "}
            <button onClick={addAnm} disabled={busy}
              className="text-brand font-semibold hover:underline disabled:opacity-50" data-testid="add-anm-btn">Přidat je</button>
          </div>
        )}
      </div>

      {/* pravý sloupec */}
      <div className="space-y-4 min-w-0">
        {seeMoney && splitTotal > 0 && (
          <div className="bg-card border border-border rounded-[14px] p-4" data-testid="team-cost-split">
            <h3 className="text-[15px] font-bold mb-3">Rozdělení nákladů</h3>
            <div className="flex items-center justify-between text-[13px] py-1"><span className="font-semibold">Vlastní lidé</span><span className="text-muted-foreground tabular-nums">{formatCZK(own)}</span></div>
            <div className="flex items-center justify-between text-[13px] py-1"><span className="font-semibold">Subdodávky</span><span className="text-muted-foreground tabular-nums">{formatCZK(sub)}</span></div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-2 flex">
              <div className="h-full bg-brand" style={{ width: `${(own / splitTotal) * 100}%` }} />
              <div className="h-full bg-brand/40" style={{ width: `${(sub / splitTotal) * 100}%` }} />
            </div>
          </div>
        )}
        {/* Oprávnění: jedna sdílená tabulka pro zakázku i Nastavení — ať se
            nerozejdou. Vidí ji jen vedení (majitel, stavbyvedoucí, asistentka). */}
        {manager && <PermissionsMatrix compact />}
      </div>

      <AlertDialog open={addAllOpen} onOpenChange={setAddAllOpen}>
        <AlertDialogContent data-testid="add-all-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Přidat všechny z firmy?</AlertDialogTitle>
            <AlertDialogDescription>
              Přidat {missing.length} lidí na zakázku {proj.name}? Už přiřazení se přeskočí.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="add-all-cancel">Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={addAll} disabled={busy} data-testid="add-all-confirm-btn">Přidat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
